import React, {
	Fragment,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { classifySourceUrl } from '@atlassian/conversation-assistant-utils/classifySourceUrl';

import type { UpdateWorkItemFieldsAction } from '../../common/types';

import type { mainUpdateWorkItemActionQuery } from './__generated__/mainUpdateWorkItemActionQuery.graphql';
import { ActionCard } from './action-card';
import { CardGroupContext } from './CardGroupContext';
import { CardGroupFooter } from './CardGroupFooter';
import { messages } from './messages';
import { SystemMessage } from './SystemMessage';
import { groupUrlsByFrequency } from './url-grouping';
import {
	actionToIssueAri,
	areBulkActionButtonsDisabled,
	buildJqlForActions,
	capitaliseActionIssueIds,
	deduplicateActions,
	groupActionsByIssue,
	validateJiraUpdateWorkItemFieldsAction,
} from './utils';

const styles = cssMap({
	cardGroup: {
		flexDirection: 'column',
		color: token('color.text'),
		backgroundColor: token('elevation.surface.raised'),
		transition: '200ms',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	cardsContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

export type JiraUpdateWorkItemFieldsActionRendererProps = ActionRendererFor<
	typeof ActionKeys.UpdateWorkItemFields
> & { showFooter?: boolean; showButtons?: boolean };

export const JiraUpdateWorkItemFieldsActionRenderer: React.MemoExoticComponent<
	({
		cloudId,
		actions,
		onConfirm,
		onCancel,
		getStatus,
		getReadonly,
		showFooter,
		showButtons,
		...props
	}: JiraUpdateWorkItemFieldsActionRendererProps) => React.JSX.Element | null
> = memo(
	({
		cloudId,
		actions,
		onConfirm,
		onCancel,
		getStatus,
		getReadonly,
		showFooter = true,
		showButtons = true,
		...props
	}: JiraUpdateWorkItemFieldsActionRendererProps): React.JSX.Element | null => {
		di(ActionCard, CardGroupFooter);

		const { sendAnalyticsEvent } = useAnalytics();
		const { formatMessage } = useIntl();

		const [isBulkActioning, setisBulkActioning] = useState(false);

		const validActions = useMemo(() => {
			const filteredActions = actions.filter(
				(action) => validateJiraUpdateWorkItemFieldsAction(action).isValid,
			);
			// Ensure issueIds are capitalised so that if an issue key provided by the LLM is in lowercase, it can still
			// be matched to an issue.
			const capitalisedIssueIdActions = capitaliseActionIssueIds(filteredActions);
			return deduplicateActions(capitalisedIssueIdActions);
		}, [actions]);

		// An "active action" is one that is not cancelled (so idle, accepted, or failed). We filter out cancelled actions so they are removed from view
		// as per product requirements. If an action card has no active actions, it will be unmounted.
		const validNonCancelledActions = useMemo(
			() =>
				validActions.filter(
					(action) => !(action.status === 'cancelled' || getStatus(action) === 'cancelled'),
				),
			[validActions, getStatus],
		);

		const allActionsCancelled = useMemo(
			() =>
				validActions.every(
					(action) => action.status === 'cancelled' || getStatus(action) === 'cancelled',
				),
			[validActions, getStatus],
		);

		const allActionsApplied = useMemo(
			() =>
				validNonCancelledActions.every(
					(action) => action.status === 'finished' || getStatus(action) === 'finished',
				),
			[validNonCancelledActions, getStatus],
		);

		// Using validActions instead of validNonCancelledActions as if validNonCancelledActions changes when a user dismisses all suggestions for an issue, we would
		// re-fetch the Relay query, and see an unexpected loading skeleton, which may be more expensive than fetching some unused data.
		const jqlString = useMemo(() => buildJqlForActions(validActions), [validActions]);

		// Retrieve a list of field keys to fetch in our relay query
		const fieldsToUpdate = useMemo(() => {
			const fieldKeysSet = new Set(validActions.map((action) => action.data.fieldKey));
			// As timespent is a custom field but we still need to fetch data from JiraTimeTrackingField, we manually add the
			// field id to the list of fields to fetch.
			if (validActions.some((action) => action.data.fieldKey === 'timespent')) {
				// Swap out timespent for timetracking to fetch the correct data from JiraTimeTrackingField. timespent will map
				// to a JiraDurationField which is not relevant for this action, and will hence render a second unrelated field suggestion.
				fieldKeysSet.add('timetracking');
				fieldKeysSet.delete('timespent');
			}

			return Array.from(fieldKeysSet).sort();
		}, [validActions]);

		const data = useLazyLoadQuery<mainUpdateWorkItemActionQuery>(
			graphql`
				query mainUpdateWorkItemActionQuery(
					$cloudId: ID!
					$jql: String!
					$fieldIdsOrAliases: [String!]!
				) {
					jira {
						issueSearch(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: 50)
							@optIn(to: "JiraSpreadsheetComponent-M1") {
							edges {
								node {
									id
									key
									...actionCard_updateWorkItemFieldsActionRenderer
										@arguments(fieldIdsOrAliases: $fieldIdsOrAliases)
									fieldsByIdOrAlias(idsOrAliases: $fieldIdsOrAliases, ignoreMissingFields: true)
										@optIn(to: "JiraIssueFieldsByIdOrAlias") {
										fieldId
										type
									}
								}
							}
						}
					}
				}
			`,
			{ cloudId, jql: jqlString, fieldIdsOrAliases: fieldsToUpdate },
		);

		// Create a mapping from action to fieldType using the key format `issueId-fieldKey` to be used in analytics.
		const actionToFieldTypeMap = useMemo(() => {
			const map = new Map<string, string>();
			const issueSearch = data.jira?.issueSearch;

			if (!issueSearch?.edges) {
				return map;
			}

			validActions.forEach((action) => {
				const issueAri = actionToIssueAri(action, cloudId, data.jira?.issueSearch?.edges);
				if (!issueAri) {
					return;
				}
				// Find matching edge with ARI
				const matchingEdge = issueSearch?.edges?.find((edge) => edge?.node?.id === issueAri);

				if (matchingEdge?.node?.fieldsByIdOrAlias) {
					// Find the field that matches the action's fieldKey
					const matchingField = matchingEdge?.node?.fieldsByIdOrAlias?.find(
						(field) => field?.fieldId === action.data.fieldKey,
					);

					// Extract field type to be used in analytics
					if (matchingField?.type) {
						map.set(issueAri, matchingField.type);
					}
				}
			});
			return map;
		}, [data.jira?.issueSearch, validActions, cloudId]);

		// Helper function used to get the field type for each action - used for sending meaningful analytics events.
		const getFieldTypeForAction = useCallback(
			(action: UpdateWorkItemFieldsAction) => {
				const issueAri = actionToIssueAri(action, cloudId, data.jira?.issueSearch?.edges);
				if (!issueAri) {
					return action.data.fieldKey;
				}
				return actionToFieldTypeMap.get(issueAri) ?? action.data.fieldKey;
			},
			[actionToFieldTypeMap, cloudId, data.jira?.issueSearch?.edges],
		);

		const onApplyAll = useCallback(async () => {
			setisBulkActioning(true);

			// Group by field type to be used for analytics
			const fields = validNonCancelledActions
				?.filter((action) => action.status === 'planned' && getStatus(action) === 'idle')
				.map((action) => getFieldTypeForAction(action));
			const fieldTypeCounts = fields.reduce(
				(acc, fieldType) => {
					acc[fieldType] = (acc[fieldType] || 0) + 1;
					return acc;
				},
				{} as Record<string, number>,
			);

			// Fire event for Apply all button being clicked
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'updateWorkApplyAll',
				attributes: {
					numFields: fields.length,
					fieldTypeCounts,
				},
			});

			// Fire confirm events for all idle actions and wait for them to complete
			await Promise.all(
				validNonCancelledActions
					.filter((action) => action.status === 'planned' && getStatus(action) === 'idle')
					.map(async (action) => {
						await onConfirm(action);
						sendAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'updateWorkAccept',
							attributes: {
								fieldType: getFieldTypeForAction(action),
								hasReasoning: !!action.data.reasoning,
								hasSourceUrl: !!action.data.sourceUrl,
								...(fg('add-source-in-update-workitem-analytics')
									? { sourceUrlType: classifySourceUrl(action.data.sourceUrl) }
									: {}),
								bulkActioned: true,
							},
						});
					}),
			);

			setisBulkActioning(false);
		}, [validNonCancelledActions, onConfirm, sendAnalyticsEvent, getFieldTypeForAction, getStatus]);

		const onDismissAll = useCallback(async () => {
			setisBulkActioning(true);

			// Group by field type to be used for analytics
			const fields = validNonCancelledActions
				?.filter((action) => action.status === 'planned' && getStatus(action) === 'idle')
				.map((action) => getFieldTypeForAction(action));
			const fieldTypeCounts = fields.reduce(
				(acc, fieldType) => {
					acc[fieldType] = (acc[fieldType] || 0) + 1;
					return acc;
				},
				{} as Record<string, number>,
			);

			// Fire event for Dismiss all button being clicked
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'updateWorkDismissAll',
				attributes: {
					numFields: fields.length,
					fieldTypeCounts,
				},
			});

			// Fire cancel events for all idle actions
			await Promise.all(
				validNonCancelledActions
					.filter((action) => action.status === 'planned' && getStatus(action) === 'idle')
					.map(async (action) => {
						await onCancel(action);
						sendAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'updateWorkDismiss',
							attributes: {
								fieldType: getFieldTypeForAction(action),
								hasReasoning: !!action.data.reasoning,
								hasSourceUrl: !!action.data.sourceUrl,
								bulkActioned: true,
							},
						});
					}),
			);

			setisBulkActioning(false);
		}, [validNonCancelledActions, onCancel, sendAnalyticsEvent, getFieldTypeForAction, getStatus]);

		const areActionButtonsDisabled = useMemo(
			() => areBulkActionButtonsDisabled(validNonCancelledActions, getStatus, getReadonly),
			[validNonCancelledActions, getStatus, getReadonly],
		);

		const actionCards = useMemo(() => {
			if (validNonCancelledActions.length === 0) {
				return null;
			}

			const issueSearch = data.jira?.issueSearch;
			if (!issueSearch) {
				return null;
			}

			const groupedActions = groupActionsByIssue(
				validNonCancelledActions,
				cloudId,
				issueSearch?.edges ?? [],
			);

			// Use reduce to build the array of action cards and filter out null values. This is so we can simply expand the first action card by default.
			return issueSearch?.edges?.reduce<ReactNode[]>((cards, edge) => {
				if (!edge?.node) {
					return cards;
				}
				const issueActions = groupedActions.get(edge.node.id);
				if (!issueActions) {
					return cards;
				}

				cards.push(
					<Fragment key={`${edge.node.id}-${edge.node.key}`}>
						<ActionCard
							cloudId={cloudId}
							queryRef={edge.node}
							actions={issueActions}
							onConfirm={onConfirm}
							onCancel={onCancel}
							getStatus={getStatus}
							getReadonly={getReadonly}
							shouldExpand={cards.length === 0}
							showButtons={showButtons}
							{...props}
						/>
					</Fragment>,
				);
				return cards;
			}, []);
		}, [
			data.jira?.issueSearch,
			validNonCancelledActions,
			cloudId,
			onConfirm,
			onCancel,
			getStatus,
			getReadonly,
			showButtons,
			props,
		]);

		// Use validActions instead of validNonCancelledActions as a cancelled action may contain a sourceUrl which provides context for the suggestions.
		const canonicalSourceUrlBase = useMemo(() => {
			const urls =
				validActions
					?.map((action) => action?.data?.sourceUrl)
					?.filter((url): url is string => typeof url === 'string' && url.length > 0) ?? [];
			if (urls.length === 0) {
				return undefined;
			}

			const groupedUrlsByFrequency = groupUrlsByFrequency(urls, { ignoreInvalid: true });

			// Return the most frequent URL's base (origin + pathname)
			return groupedUrlsByFrequency?.[0]?.base;
		}, [validActions]);

		useEffect(() => {
			if (actionCards && actionCards.length > 0) {
				const fields = validActions
					.filter((action) => action.status === 'planned' && getStatus(action) === 'idle')
					.map((action) => getFieldTypeForAction?.(action));

				// Group by field type to be used for analytics
				const fieldTypeCounts = fields.reduce(
					(acc, fieldType) => {
						acc[fieldType] = (acc[fieldType] || 0) + 1;
						return acc;
					},
					{} as Record<string, number>,
				);

				const sourceUrlAttributes = fg('add-source-in-update-workitem-analytics')
					? {
							hasSourceUrl: canonicalSourceUrlBase !== undefined,
							sourceUrlType: classifySourceUrl(canonicalSourceUrlBase),
						}
					: {};

				sendAnalyticsEvent({
					action: 'rendered',
					actionSubject: 'component',
					actionSubjectId: 'updateWorkActionRenderer',
					attributes: {
						numActionCards: actionCards.length,
						numFields: fields.length,
						fieldTypeCounts,
						...sourceUrlAttributes,
					},
				});
			}
			// Empty dependency array to ensure that we only fire the analytics event once when the component renders and there are action cards.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		// When all valid actions are cancelled, render the system message
		if (allActionsCancelled) {
			return <SystemMessage message={formatMessage(messages.allActionsDismissed)} />;
		}

		if (actionCards?.length === 0 || !actionCards) {
			return null;
		}

		return (
			<Stack space="space.150">
				<CardGroupContext sourceUrl={canonicalSourceUrlBase} />
				<Flex
					xcss={styles.cardGroup}
					testId="update-work-item-fields-action-renderer.ui.action-card-group"
				>
					<Stack xcss={styles.cardsContainer}>{actionCards}</Stack>
					{showFooter && (
						<CardGroupFooter
							onDismiss={onDismissAll}
							onApplyUpdates={onApplyAll}
							isDisabled={areActionButtonsDisabled}
							allActionsApplied={allActionsApplied}
							isBulkActioning={isBulkActioning}
						/>
					)}
				</Flex>
				{allActionsApplied && (
					<SystemMessage message={formatMessage(messages.allActionsAccepted)} />
				)}
			</Stack>
		);
	},
);
